import React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import { useTwilioCall } from "./TwilioCallProvider";

const buildCustomerRoute = (incomingMeta = {}) => {
  const customerId =
    incomingMeta?.customerId ||
    incomingMeta?.customerData?.Id ||
    incomingMeta?.customerData?.id;

  if (!customerId) return null;

  return `/customer-single/${customerId}/overview`;
};

const TwilioIncomingCallPopup = () => {
  const history = useHistory();

  const {
    CALL_PHASE,
    callPhase,
    callStatus,
    incomingCaller,
    incomingMeta,
    acceptIncomingCall,
    rejectIncomingCall,
    endCall,
    passCall,
  } = useTwilioCall();

  const isIncoming = callPhase === CALL_PHASE.INCOMING;
  const isConnected = callPhase === CALL_PHASE.CONNECTED;

  if (!isIncoming && !isConnected) return null;

  const displayName =
    incomingMeta?.customerName || incomingCaller || "Unknown caller";

  const customerRoute = buildCustomerRoute(incomingMeta);

  const handleAnswerAndOpen = () => {
    acceptIncomingCall({
      openCustomer: true,
      onOpenCustomer: () => {
        if (customerRoute) {
          history.push(customerRoute);
        }
      },
    });
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        right: 24,
        bottom: 24,
        width: 360,
        maxWidth: "calc(100vw - 32px)",
        zIndex: 1600,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(34, 197, 94, 0.35)",
      }}
    >
      <Box sx={{ p: 2, background: "rgba(34, 197, 94, 0.06)" }}>
        <Typography variant="caption" color="text.secondary">
          {isIncoming ? "Incoming call" : "Active call"}
        </Typography>

        <Typography variant="subtitle1" fontWeight={800}>
          {displayName}
        </Typography>

        {incomingMeta?.phone && (
          <Typography variant="body2" color="text.secondary">
            {incomingMeta.phone}
          </Typography>
        )}

        {incomingMeta?.customerId && (
          <Typography variant="caption" color="text.secondary" display="block">
            Customer ID: {incomingMeta.customerId}
          </Typography>
        )}

        {incomingMeta?.leadId && (
          <Typography variant="caption" color="text.secondary" display="block">
            Lead ID: {incomingMeta.leadId}
          </Typography>
        )}

        {incomingMeta?.customerData?.Email && (
          <Typography variant="caption" color="text.secondary" display="block">
            Email: {incomingMeta.customerData.Email}
          </Typography>
        )}

        {incomingMeta?.customerData?.BusinessName && (
          <Typography variant="caption" color="text.secondary" display="block">
            Business: {incomingMeta.customerData.BusinessName}
          </Typography>
        )}

        {incomingMeta?.source && (
          <Chip
            size="small"
            label={incomingMeta.source}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          {callStatus}
        </Typography>
      </Box>

      <Stack spacing={1} sx={{ p: 1.5 }}>
        {isIncoming && (
          <>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={() => acceptIncomingCall()}
              startIcon={<i className="fa-solid fa-phone" />}
            >
              Answer
            </Button>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={!customerRoute}
              onClick={handleAnswerAndOpen}
              startIcon={<i className="fa-solid fa-user" />}
            >
              Answer + Open Customer
            </Button>

            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={passCall}
              startIcon={<i className="fa-solid fa-share" />}
            >
              Pass Call
            </Button>

            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={rejectIncomingCall}
              startIcon={<i className="fa-solid fa-phone-slash" />}
            >
              Decline
            </Button>
          </>
        )}

        {isConnected && (
          <>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={passCall}
              startIcon={<i className="fa-solid fa-share" />}
            >
              Pass / Transfer Coming Soon
            </Button>

            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={endCall}
              startIcon={<i className="fa-solid fa-phone-slash" />}
            >
              End Call
            </Button>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default TwilioIncomingCallPopup;
